import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getSettings,
  updateSettings,
} from "../../../services/settingsServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FaSave, FaMoon, FaSun } from "react-icons/fa";

/* Quill */
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
};
const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

const InfoClubForm = () => {
  const [settings, setSettings] = useState({
    infoclub_isactive: true,
    infoclub_title: "",
    infoclub_content: "",
    logo_club: "",
  });
  const [loading, setLoading] = useState(true);

  // Aperçu
  const [previewDark, setPreviewDark] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsData = await getSettings();
        if (Array.isArray(settingsData) && settingsData.length > 0) {
          setSettings((prev) => ({ ...prev, ...settingsData[0] }));
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
        toast.error("Impossible de charger les paramètres.");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContentChange = (name, value) => {
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveSettings = async () => {
    try {
      await updateSettings(settings);
      toast.success("Paramètres mis à jour avec succès");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour des paramètres");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveSettings();
  };

  const previewSettings = useMemo(
    () => ({
      isActive: !!settings.infoclub_isactive,
      title: settings.infoclub_title || "Informations du club",
      content:
        settings.infoclub_content ||
        "<p>Ajoutez ici vos informations importantes : horaires, actu, rappel, etc.</p>",
      logo: settings.logo_club || "/img/club_logo_no-image.jpg",
    }),
    [settings],
  );

  if (loading) {
    return (
      <section className="w-full">
        <div className="mx-auto max-w-7xl px-1 sm:px-1">
          <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8">
              <div className="h-8 w-64 bg-white/25 rounded animate-pulse" />
            </div>
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-24 rounded-xl bg-slate-100/80 dark:bg-slate-800/40 animate-pulse"
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-1 sm:px-1">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* HEADER */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                Info Club — Paramètres & Prévisualisation
              </h2>
              <p className="text-white/90 text-sm sm:text-[15px] mt-1">
                Gérez l’encart « Info Club » de la page d’accueil avec aperçu en
                direct.
              </p>
            </div>
            <Button
              text="Enregistrer"
              icon={FaSave}
              onClick={saveSettings}
              className="!bg-white !text-slate-900 !hover:bg-white/90 !shadow-md !rounded-xl gap-2"
            />
          </div>

          {/* CONTENT: Form + Preview */}
          <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Activation */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                      Activer la section Info Club
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      Contrôle l’affichage sur la page d’accueil.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setSettings((prev) => ({
                        ...prev,
                        infoclub_isactive: !prev.infoclub_isactive,
                      }))
                    }
                    className={`w-14 h-7 rounded-full p-1 transition-colors ${
                      settings.infoclub_isactive
                        ? "bg-green-500"
                        : "bg-slate-300 dark:bg-slate-700"
                    }`}
                    title={
                      settings.infoclub_isactive ? "Désactiver" : "Activer"
                    }
                  >
                    <span
                      className={`h-5 w-5 bg-white rounded-full shadow transform transition-transform ${
                        settings.infoclub_isactive
                          ? "translate-x-7"
                          : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Titre */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Titre de la section
                </label>
                <input
                  type="text"
                  name="infoclub_title"
                  value={settings.infoclub_title || ""}
                  onChange={handleChange}
                  placeholder="Ex : Informations du club"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* Contenu */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Contenu
                </label>
                <div
                  className="mt-1
                  [&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-slate-300 dark:[&_.ql-toolbar]:border-slate-700
                  [&_.ql-toolbar]:bg-white/80 dark:[&_.ql-toolbar]:bg-slate-900/70
                  [&_.ql-container]:rounded-b-xl [&_.ql-container]:border-slate-300 dark:[&_.ql-container]:border-slate-700
                  [&_.ql-container]:bg-white/80 dark:[&_.ql-container]:bg-slate-900/70"
                >
                  <ReactQuill
                    value={settings.infoclub_content || ""}
                    onChange={(v) => handleContentChange("infoclub_content", v)}
                    modules={modules}
                    formats={formats}
                    placeholder="<p>Votre message important ici…</p>"
                  />
                </div>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Astuce : utilisez le gras pour les éléments importants.
                  (Aperçu ci-dessous)
                </p>
              </div>

              {/* Actions bas */}
              <div className="flex justify-end">
                <Button
                  text="Mettre à jour"
                  type="submit"
                  className="!rounded-xl gap-2"
                  icon={FaSave}
                />
              </div>
            </form>

            {/* Prévisualisation temps réel */}
            <div className="space-y-4">
              {/* Toggle Dark/Light */}
              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => setPreviewDark((d) => !d)}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 px-3 py-2 text-sm inline-flex items-center gap-2 hover:bg-white/90 dark:hover:bg-slate-800"
                >
                  {previewDark ? <FaSun /> : <FaMoon />}
                  {previewDark ? "Clair" : "Sombre"}
                </button>
              </div>

              <div className={`${previewDark ? "dark" : ""}`}>
                <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-5">
                  {!previewSettings.isActive ? (
                    <div className="text-sm text-slate-600 dark:text-slate-400 italic">
                      La section Info Club est <b>désactivée</b>. (Elle
                      n’apparaîtra pas sur l’accueil.)
                    </div>
                  ) : (
                    <section className="py-6 px-4 bg-lightBackground dark:bg-darkBackground text-black dark:text-white max-w-[1280px] mx-auto">
                      <div className="bg-gradient-to-r from-slate-100 to-slate-200 dark:from-gray-700 dark:to-gray-800 rounded-2xl shadow-xl flex flex-col lg:flex-row overflow-hidden border border-gray-300 dark:border-gray-800">
                        {/* Contenu */}
                        <div className="lg:w-2/3 w-full p-8 flex flex-col justify-center font-montserrat">
                          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white mb-4">
                            {previewSettings.title}
                          </h2>
                          <div
                            className="infoclub-content text-gray-700 dark:text-gray-300 leading-relaxed text-justify"
                            dangerouslySetInnerHTML={{
                              __html: previewSettings.content,
                            }}
                          />
                        </div>
                        {/* Image */}
                        <div className="lg:w-1/3 w-full max-h-[300px] lg:max-h-full">
                          <img
                            src="/img/infoclub.png"
                            alt={previewSettings.title || "Info Club"}
                            className="object-cover h-full w-full"
                            onError={(e) => {
                              e.currentTarget.src = "/img/infoclub.png";
                            }}
                          />
                        </div>
                      </div>
                    </section>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Styles locaux */}
      <style>{`
        /* Placeholders */
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }

        /* Corrige le contraste du gras en dark pour le contenu Quill/HTML */
        .dark .infoclub-content strong,
        .dark .infoclub-content b { color: #fff; }

        /* Apple-friendly */
        html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>
    </section>
  );
};

export default InfoClubForm;
